// src/App.js
import React, { lazy, Suspense, useCallback, Fragment, useEffect, useState, useRef } from 'react';
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useSearchParams,
  useLocation,
} from 'react-router-dom';
import axios from 'axios';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorFallback from './pages/ErrorFallback/ErrorFallback';


import getCMResponse, { getShopifyResponse } from './utils/api_manager';
import Config from './utils/Config';
import { useCurrentProject, useCurrentProjectWriter } from './utils/redux/currentProjectService';
import { useShopifyEmbeddedBootstrap } from './utils/shopify/useShopifyEmbeddedBootstrap';
import { isShopifyEmbeddedContext, getShopifyContext } from './utils/shopify/embeddedContext';
import Login from './pages/Login/Login';
import CreateProject from './pages/CreateProject/CreateProject';
import GrantAccess from './pages/GrantAccess/GrantAccess';
import AcceptInvite from './pages/AcceptInvite/AcceptInvite';
import AcceptOwnershipTransfer from './pages/AcceptOwnershipTransfer/AcceptOwnershipTransfer';
import ProjectRouteGuard from './components/ProjectRouteGuard/ProjectRouteGuard';
import LegacyProjectRedirect from './components/LegacyProjectRedirect/LegacyProjectRedirect';
import AppLayout from './components/AppLayout/AppLayout';
import PageErrorBoundary from './components/Errors/PageErrorBoundary/PageErrorBoundary';
import PageLoader from './components/PageLoader/PageLoader';
import styles from './App.module.css';
import { getPostAuthRoute, DEFAULT_LANDING_ROUTE, isValidRedirectUrl } from './utils/postAuthRoute';

// Session-signal-driven auth guard (Option B): tokens are httpOnly, so the
// only way to know whether a session exists is the outcome of the
// /user/profile call itself. On failure (no valid session), we send the
// user to "/login" and, if they were trying to reach some other path,
// preserve that path in a `redirect_url` query param so it can be restored
// after login. These two helpers centralize that capture/restore so the
// logic isn't duplicated at each call site below.
export const buildLoggedOutRedirect = (currentPathname, currentSearch) => {
  if (currentPathname === '/' && !currentSearch) {
    return '/login';
  }
  const originalPath = `${currentPathname}${currentSearch || ''}`;
  return `/login?redirect_url=${encodeURIComponent(originalPath)}`;
};

export const consumeRedirectUrl = (search) => {
  const params = new URLSearchParams(search);
  const value = params.get('redirect_url');
  return isValidRedirectUrl(value) ? value : null;
};

const Analyze = lazy(() => import('./pages/Analyze/Analyze'));
const Organizations = lazy(() => import('./pages/Organizations/Organizations'));
const Projects = lazy(() => import('./pages/Projects/Projects'));
const Settings = lazy(() => import('./pages/Settings/Settings'));
const VisibilityPlanner = lazy(() => import('./pages/VisibilityPlanner/VisibilityPlanner'));
const NotFound = lazy(() => import('./pages/NotFound/NotFound'));
const MetaOAuthCallback = lazy(() => import('./pages/OAuthCallback/MetaOAuthCallback'));
const InstagramOAuthCallback = lazy(() => import('./pages/OAuthCallback/InstagramOAuthCallback'));

const App = () => {
  const location = useLocation();

  const [user, setUser] = useState({});
  // currentProject now lives in Redux (sessionStorage-persisted) rather
  // than local useState, so it survives the full-page navigation a Shopify
  // OAuth grant performs -- see docs/product/bugs/project-access-scoping-trd.md.
  // `|| {}` preserves the pre-existing {}-not-null contract every downstream
  // consumer already relies on (e.g. currentProject?.id, .platforms).
  // `setCurrentProject` here is the wrapper service's writer, not a raw
  // useState setter -- kept under the same name so every existing call site
  // below needs no further change.
  const currentProject = useCurrentProject() || {};
  const { setCurrentProject } = useCurrentProjectWriter();
  const [profileLoading, setProfileLoading] = useState(true);
  const [dashboards, setDashboards] = useState(null);
  const dashboardsFetchedForRef = useRef(null);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  axios.defaults.withCredentials = true;

  // Shopify embedded-surface bootstrap (TRD: shopify-embedded-install-auth,
  // Bounded Unit B, B1). `initialSearch` is only read inside the hook's
  // once-run effect, so passing the current render's location.search here
  // is safe -- it captures the value at mount time, which is the only load
  // that can ever carry Shopify's embedded query params.
  const shopifyBootstrap = useShopifyEmbeddedBootstrap({ initialSearch: location.search });
  const shopifyStubAppliedRef = useRef(false);

  const initUser = useCallback(() => {
    getCMResponse('/user/profile', 'GET').then(user => {
      setUser(user.data);

      const projects = user.data.projects || [];
      const pendingInvitations = user.data.pending_invitations || [];
      const hasProject = projects.length > 0;
      const redirectUrl = consumeRedirectUrl(location.search);

      if (hasProject) {
        // connect-cta-new-tab-project-leak-trd.md Section 2.3/3, R3:
        // the "match persisted value against fresh projects" branch this
        // used to have is gone -- there is no persisted value to match
        // against anymore for the general case. nextProject is
        // unconditionally projects[0]; correctness for an
        // already-project-scoped URL is ProjectRouteGuard's job (it
        // will immediately overwrite this with the URL-derived value the
        // moment the resulting navigation lands on a scoped route), not
        // initUser's.
        const nextProject = projects[0];
        setCurrentProject(nextProject);
        if (redirectUrl) {
          navigate(redirectUrl, { replace: true });
        } else if (location.pathname.includes('/login')) {
          navigate(getPostAuthRoute(projects, pendingInvitations, nextProject), { replace: true });
        }
      } else if (redirectUrl) {
        navigate(redirectUrl, { replace: true });
      } else {
        const target = getPostAuthRoute(projects, pendingInvitations, {});
        if (!location.pathname.includes(target)) {
          navigate(target, { replace: true });
        }
      }
    }).catch(err => {
      console.log(err);
      // No valid session (profile call failed, e.g. 401): this is the only
      // signal we have since access/refresh tokens are httpOnly. By this
      // point getCMResponse has already exhausted its own silent-refresh
      // attempt (see api_manager.js), so this is a true full-session expiry
      // or a never-authenticated user. We deliberately do NOT navigate here:
      // once .finally() below flips profileLoading to false, the unified
      // route-guard effect (below, near the /analyze/* recheck) re-runs,
      // sees `user` is still {} (not authenticated), and performs the
      // /login + redirect_url navigation itself via buildLoggedOutRedirect.
      // Keeping that as the single call site for "logged-out user hits a
      // blocked route -> /login" avoids duplicating the decision here too.
    }).finally(() => {
      setProfileLoading(false);
    });
  }, [location, navigate, setCurrentProject]);

  // Called by CreateProject on a successful POST /project. Keeps the
  // in-memory projects list (derived from `user.projects`) and the
  // active project in sync, then routes via the same shared decision
  // function used everywhere else.
  const handleProjectCreated = useCallback((newProject) => {
    const updatedProjects = [...(user.projects || []), newProject];
    setUser(prev => ({ ...prev, projects: [...(prev.projects || []), newProject] }));
    setCurrentProject(newProject);
    navigate(getPostAuthRoute(updatedProjects, user.pending_invitations || [], newProject), { replace: true });
  }, [user, navigate, setCurrentProject]);

  // Called by the TopNav project switcher when the user picks a different
  // joined project. Re-runs the same shared redirect logic so switching to
  // a project with zero connected platforms sends the user to
  // /grant-access, exactly like a freshly created project would.
  const handleSwitchProject = useCallback((project) => {
    setCurrentProject(project);
    const projects = user.projects || [];
    navigate(getPostAuthRoute(projects, user.pending_invitations || [], project), { replace: true });
  }, [user, navigate, setCurrentProject]);

  // B5 (TRD Bounded Unit B), RECONCILED against cq-api's actual shipped A4:
  // cq-api did NOT add a new `/analyze/dashboards?surface=shopify` route --
  // it extended this SAME existing `/analyze/project/:projectId`
  // endpoint with `verifyAuthOrShopifySession` (see
  // cq-api/router/dashboard/dashboard.router.js). Filtering to
  // shopify_capable dashboards happens automatically server-side based on
  // which auth surface the request used (`req.authSurface === 'shopify'`,
  // set by the middleware from a Bearer session token vs. a JWT cookie) --
  // no query params are read by the route at all, and the response shape
  // is unchanged. So the only thing that needs to vary here is the
  // request's auth TRANSPORT (getShopifyResponse's fresh Bearer session
  // token vs. getCMResponse's cookie), not the URL/params -- confirmed
  // directly against cq-api's shipped route and service code, not assumed.
  const refreshDashboards = useCallback((projectId) => {
    if (!projectId) return;
    const request = isShopifyEmbeddedContext() ? getShopifyResponse : getCMResponse;

    request(`/analyze/project/${projectId}`, 'GET')
      .then(response => {
        if (response.success && response.data && response.data.dashboards) {
          setDashboards(response.data.dashboards);
        }
      })
      .catch(err => {
        console.error('Error fetching dashboards:', err);
      });
  }, []);

  useEffect(() => {
    const id = currentProject?.id;
    if (!id) return;
    // Only fetch once per project id; refreshDashboards can force a re-fetch
    if (dashboardsFetchedForRef.current === id) return;
    dashboardsFetchedForRef.current = id;
    refreshDashboards(id);
  }, [currentProject, refreshDashboards]);
  

  const validateAuth = useCallback(() => {
    var gtkn = searchParams.get('gcode');
    if (gtkn != null) {
      axios.post(Config.BASE_URL + '/auth/google',
        { "gcode": gtkn },
        {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        },
      ).then(responseJSON => {
        const projects = user.projects || [];
        const pendingInvitations = user.pending_invitations || [];
        const hasProject = projects.length > 0;
        const nextProject = hasProject ? projects[0] : {};
        const redirectUrl = consumeRedirectUrl(location.search);

        navigate(
          redirectUrl || getPostAuthRoute(projects, pendingInvitations, nextProject),
          { replace: true }
        );

        if (hasProject) {
          setCurrentProject(nextProject);
        }
      }).catch(err => {
        console.log(err);
      }).finally(() => {
        setProfileLoading(false);
      });
    } else {
      initUser();
    }
  }, [searchParams, initUser, navigate, user, location, setCurrentProject]);

  useEffect(() => {
    // Shopify embedded surface (TRD Bounded Unit B, B1/B3): authenticated
    // per-request via an App Bridge session token, not the httpOnly
    // JWT-cookie flow validateAuth()/initUser() drive -- there is no
    // /user/profile call to make here. shopifyBootstrap.isShopifyEmbedded
    // is already correct on this very first render (computed synchronously
    // from the initial URL/sessionStorage, not from an effect), so this
    // check is safe to run once on mount exactly like the call it guards.
    if (shopifyBootstrap.isShopifyEmbedded) return;
    validateAuth();
  }, []);

  // Applies the Shopify session-exchange result (TRD A1) into the same
  // user/currentProject slots the standalone cookie-auth flow uses, once
  // -- reusing existing project-scoped routing (ProjectRouteGuard,
  // AppLayout's isLoggedIn check) rather than forking a second render tree.
  // `role` is deliberately omitted from the stub project: Shopify-surface
  // authorization is governed by isShopifyAccountOwner (B6), not
  // CliqMetrics's website role matrix (TRD A3) -- see Settings.jsx.
  useEffect(() => {
    if (!shopifyBootstrap.isShopifyEmbedded) return;
    if (shopifyBootstrap.bootstrapping || shopifyBootstrap.error) return;
    if (!shopifyBootstrap.projectId) return;
    if (shopifyStubAppliedRef.current) return;
    shopifyStubAppliedRef.current = true;

    const stubProject = { id: shopifyBootstrap.projectId };
    setUser({
      projects: [stubProject],
      pending_invitations: [],
      _shopifyEmbedded: true,
    });
    setCurrentProject(stubProject);
    setProfileLoading(false);

    // Landing route decision: FOLLOW-UP flagged in completion summary --
    // the TRD doesn't mandate a specific embedded landing route. Reuses
    // is_new_install (a confirmed A1 response field) the same way a
    // freshly created standalone project routes through
    // handleProjectCreated -> getPostAuthRoute's zero-platforms branch
    // above: first-time installs land on grant-access, returning installs
    // go straight to the (B5-filtered) dashboards list.
    const target = shopifyBootstrap.isNewInstall
      ? `/${shopifyBootstrap.projectId}/grant-access`
      : `/${shopifyBootstrap.projectId}/analyze/dashboards/all`;
    navigate(target, { replace: true });
  }, [
    shopifyBootstrap.isShopifyEmbedded,
    shopifyBootstrap.bootstrapping,
    shopifyBootstrap.error,
    shopifyBootstrap.projectId,
    shopifyBootstrap.isNewInstall,
    navigate,
    setCurrentProject,
  ]);

  // B4.2: a direct, non-embedded hit on a CliqMetrics route from a
  // Shopify-origin visitor (stale bookmark, or the URL bar reloaded outside
  // the iframe) redirects into the embedded admin context instead of
  // rendering our own UI un-framed. Conservative, positive-evidence-only
  // signal -- never fires for an ordinary non-Shopify visitor:
  //   - window.top === window.self, i.e. PROVABLY not inside an iframe --
  //     this is also what satisfies B4.5's guardrail: this code path can
  //     never execute while framed, so it can never itself violate
  //     Shopify's CSP by navigating from inside the iframe.
  //   - AND either this tab previously established a real embedded session
  //     (persisted shop), or this exact load carries a `shop` query param
  //     AND the visitor has no standalone CliqMetrics auth (user is
  //     unauthenticated) -- an already-authenticated standalone user must
  //     never be bounced just because a `shop` param happens to be present.
  useEffect(() => {
    if (window.top !== window.self) return;
    if (profileLoading) return;

    const persistedShop = getShopifyContext()?.shop;
    const shopParam = searchParams.get('shop');
    const shop = persistedShop || (shopParam && Object.keys(user).length === 0 ? shopParam : null);
    if (!shop) return;

    if (!Config.SHOPIFY_APP_HANDLE) {
      console.error('[shopify] REACT_APP_SHOPIFY_APP_HANDLE not set; cannot redirect into embedded context.');
      return;
    }
    const shopHandle = shop.replace(/\.myshopify\.com$/, '');
    window.location.href = `https://admin.shopify.com/store/${shopHandle}/apps/${Config.SHOPIFY_APP_HANDLE}`;
  }, [profileLoading, searchParams, user]);

  // Unified route guard: the single place that decides "is this user logged
  // out, and if so where do they go" -- plus a distinct, authenticated-only
  // concern (missing platform connections on /analyze/*) that piggybacks on
  // the same effect for convenience. This replaces what used to be two
  // independent mechanisms: initUser's .catch used to navigate directly on
  // profile-fetch failure, while this effect (previously /analyze/*-only)
  // separately called getPostAuthRoute() with no idea whether the user was
  // even authenticated -- for a logged-out user that produced
  // getPostAuthRoute([], [], {}) = '/create-project', racing with and
  // sometimes beating the /login redirect. Folding both into one effect
  // means there is exactly one function (buildLoggedOutRedirect) and one
  // call site for "logged-out user hits a blocked route -> /login", so the
  // two can no longer drift out of sync with each other -- the same defect
  // class the PRD's Problem Statement describes (a logged-out user's empty
  // projects array being indistinguishable from "no projects yet").
  useEffect(() => {
    if (profileLoading) return;
    // The /login route element itself already handles the
    // authenticated-redirect-away case (see <Route path="/login"> below) --
    // this effect must not fight it or create a redirect loop.
    if (location.pathname === '/login') return;

    const isAuthenticated = Object.keys(user).length > 0;

    if (!isAuthenticated) {
      // No valid session: applies to ANY route (per PRD Section 4 item 1),
      // not just /analyze/* -- this is what closes the gap that previously
      // let a logged-out /analyze/* visit fall through to /create-project.
      const target = buildLoggedOutRedirect(location.pathname, location.search);
      navigate(target, { replace: true });
      return;
    }

    // Authenticated-only concern, distinct from the logged-out case above:
    // getPostAuthRoute() is normally consulted right after login, project
    // creation, and project switching (see call sites above), but none of
    // those run when an already-authenticated user directly loads or
    // client-side-navigates into a gated route (e.g. reloading
    // /analyze/dashboards/all, or following a stale link) while the active
    // project has zero connected platforms. Re-check on every /analyze/*
    // path change so that case is caught too, without duplicating the "does
    // this project have platform access" rule anywhere else. Scoped to
    // /analyze/* only -- unlike the logged-out branch above, this does not
    // apply to every route.
    // connect-cta-new-tab-project-leak-trd.md Section 3, R3: /analyze/*
    // is now project-scoped (/:projectId/analyze/*), so this can no
    // longer match on a bare '/analyze' prefix.
    if (!/^\/[^/]+\/analyze(\/|$)/.test(location.pathname)) return;

    const urlProjectIdMatch = location.pathname.match(/^\/([^/]+)/);
    const urlProjectId = urlProjectIdMatch ? urlProjectIdMatch[1] : null;
    const projects = user.projects || [];
    const urlProjectIsKnown = urlProjectId && projects.some((p) => p.id === urlProjectId);

    // currentProject has not yet caught up to the URL's own :projectId (e.g.
    // initUser()'s projects[0] default landed first, and ProjectRouteGuard's
    // URL-derived correction is still pending a render pass). Do nothing this
    // run -- this effect re-runs when currentProject changes (own dependency
    // array), and once ProjectRouteGuard corrects it to match the URL, this
    // check passes and the effect proceeds normally. Prevents firing a real
    // navigate() off a transient, about-to-be-corrected stale project value
    // (docs/product/bugs/new-tab-link-wrong-project-unscoped-cta-url,
    // refresh-reset finding).
    if (urlProjectIsKnown && currentProject?.id !== urlProjectId) {
      return;
    }

    // Shopify embedded surface (TRD B): the stub project applied above
    // has no `platforms` data (session-exchange's contract doesn't return
    // it), so getPostAuthRoute's zero-platforms check would misfire here on
    // every /analyze/* visit for a returning (non-new) install, bouncing it
    // back to grant-access. The one landing-route decision this surface
    // needs (new-install vs. returning) is already made once, up-front, by
    // the bootstrap effect above -- this generic website-flow recheck does
    // not apply to the embedded surface at all.
    if (shopifyBootstrap.isShopifyEmbedded) return;

    const pendingInvitations = user.pending_invitations || [];
    const target = getPostAuthRoute(projects, pendingInvitations, currentProject);
    // getPostAuthRoute's "no redirect needed" signal is now a
    // project-prefixed DEFAULT_LANDING_ROUTE (e.g. `/sf-1/analyze/insights`),
    // not the bare constant -- comparing against the bare DEFAULT_LANDING_ROUTE
    // here would never match once a project exists, turning every
    // /analyze/* subpage view into an unwanted redirect back to the
    // insights homepage. Compare against the same project-prefixed shape
    // getPostAuthRoute actually returns for the "fully onboarded" case.
    const defaultTargetForProject = currentProject?.id
      ? `/${currentProject.id}${DEFAULT_LANDING_ROUTE}`
      : DEFAULT_LANDING_ROUTE;

    if (target !== defaultTargetForProject && !location.pathname.includes(target)) {
      navigate(target, { replace: true });
    }
  }, [profileLoading, location.pathname, location.search, user, currentProject, navigate, shopifyBootstrap.isShopifyEmbedded]);

  // Shopify embedded session-exchange failure (TRD B3 acceptance criteria):
  // a clear in-iframe error state, never a top-level redirect to any
  // standalone login/error page -- App Bridge/Shopify may not even permit
  // navigating this frame to /login, and doing so would defeat the point of
  // staying embedded.
  if (shopifyBootstrap.isShopifyEmbedded && shopifyBootstrap.error) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center' }}>
        <p>{shopifyBootstrap.error}</p>
      </div>
    );
  }

  if (profileLoading) {
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <PageLoader label="Loading" />
      </div>
    );
  }

  return (
    <Fragment>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <Suspense fallback={<PageLoader />}>
          <AppLayout
            currentUser={user}
            currentProject={currentProject}
            projects={user.projects || []}
            pendingInvitations={user.pending_invitations || []}
            dashboards={dashboards}
            refreshDashboards={refreshDashboards}
            initUser={initUser}
            setUser={setUser}
            setCurrentProject={handleSwitchProject}
          >
            <PageErrorBoundary key={location.pathname}>
              <Routes>
                <Route exact path="/" element={
                  consumeRedirectUrl(location.search)
                    ? null
                    : (Object.keys(user).length > 0
                      ? <Navigate
                          to={
                            (user.projects || []).length > 0
                              ? `/${user.projects[0].id}/analyze/dashboards/all`
                              : getPostAuthRoute([], user.pending_invitations || [], {})
                          }
                          replace
                        />
                      : null)
                } />
                <Route exact path="/create-project" element={
                  <CreateProject onProjectCreated={handleProjectCreated} />
                } />
                {/* Project-ID-in-URL routing (TRD:
                    connect-cta-new-tab-project-leak/trd.md, Section 2.2,
                    3 R1/R2). ProjectRouteGuard is the layout element:
                    it's the only place that writes currentProject for
                    the general case, sourcing it from the URL's
                    :projectId segment rather than any client-side
                    storage -- a brand-new tab gets its own URL carrying its
                    own project ID, which is what closes this bug. */}
                <Route
                  path="/:projectId"
                  element={
                    <ProjectRouteGuard
                      projects={user.projects || []}
                      pendingInvitations={user.pending_invitations || []}
                    />
                  }
                >
                  <Route path="grant-access" element={
                    <GrantAccess currentProject={currentProject} />
                  } />
                  <Route path="analyze/*" element={<Analyze currentUser={user} currentProject={currentProject} dashboards={dashboards} refreshDashboards={refreshDashboards} />} />
                  <Route path="settings" element={<Settings currentProject={currentProject} />} />
                  <Route path="visibility-planner" element={<VisibilityPlanner currentProject={currentProject} />} />
                </Route>
                {/* Legacy-alias route: the permanent landing target of
                    cq-api's Shopify OAuth callback's cookie-missing failure
                    branch (shopify.router.js redirects here whenever the
                    sfid/nonce cookies fail to round-trip), and a catcher for
                    any stale bare /grant-access bookmark/link from before
                    project-scoped routing existed. The equivalent bare
                    /analyze/* and /settings catchers were removed as
                    confirmed-dead bookmark-only weight (pre-launch, zero
                    real users) -- this one stays; it is not bookmark-only. */}
                <Route exact path="/grant-access" element={
                  <LegacyProjectRedirect
                    projects={user.projects || []}
                    pendingInvitations={user.pending_invitations || []}
                  />
                } />
                <Route exact path="/accept-invite" element={
                  <AcceptInvite
                    user={user}
                    initUser={initUser}
                    setCurrentProject={setCurrentProject}
                  />
                } />
                {/* Project Role-Enforcement Middleware TRD, Section 3.4.
                    Mirrors /accept-invite's ?inviteId=<id> query-param
                    convention with ?transferId=<id>. Deliberately NOT folded
                    into getPostAuthRoute's automatic redirect decision order
                    -- the invitee only reaches this page via the emailed
                    acceptUrl link, and whether a pending ownership transfer
                    should pre-empt normal post-auth routing the way a
                    pending plain invitation does is out of scope for this
                    unit (TRD Section 3.4, explicit follow-up flag). */}
                <Route exact path="/accept-ownership-transfer" element={
                  <AcceptOwnershipTransfer
                    user={user}
                    initUser={initUser}
                  />
                } />
                <Route path="/login" element={
                  Object.keys(user).length > 0
                    ? <Navigate to={getPostAuthRoute(user.projects || [], user.pending_invitations || [], currentProject)} replace />
                    : <Login initUser={initUser} />
                } />
                <Route exact path="/organizations/*" element={<Organizations currentUser={user} />} />
                <Route path="/projects/*" element={<Projects />} />
                <Route exact path="/oauth/meta/callback" element={<MetaOAuthCallback />} />
                <Route exact path="/oauth/instagram/callback" element={<InstagramOAuthCallback />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </PageErrorBoundary>
          </AppLayout>
        </Suspense>
      </ErrorBoundary>
    </Fragment>
  );
};

export default App;
